<template>
  <div class="clinical-risk-wrapper">
    <!-- Badge clicável -->
    <div
      v-if="clinicalRisk && !loading"
      class="clinical-risk-badge mr-2"
      :class="badgeClass"
      @click="openModal"
      title="Clique para ver o detalhamento"
    >
      <i :class="badgeIcon" class="mr-1"></i>
      <span>Clínico: {{ badgeLabel }}</span>
      <span class="clinical-risk-score">{{ clinicalRisk.score }}pts</span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="clinical-risk-badge clinical-risk-loading">
      <b-spinner small class="mr-1" />
      <span>Calculando risco...</span>
    </div>

    <!-- Modal de detalhamento -->
    <div v-if="showModal" class="clinical-risk-modal" @click.self="closeModal">
      <div class="clinical-risk-modal-content">
        <!-- Header -->
        <div class="clinical-risk-modal-header" :class="headerClass">
          <div class="clinical-risk-modal-header-info">
            <i class="fas fa-heartbeat mr-2" style="font-size: 24px"></i>
            <div>
              <h3 class="m-0">Score de Risco Clínico</h3>
              <p class="m-0" style="opacity: 0.9; font-size: 13px">
                Última atualização: {{ updatedAt }}
              </p>
            </div>
          </div>
          <button @click="closeModal" class="clinical-risk-close-btn">
            &times;
          </button>
        </div>

        <!-- Classificação -->
        <div class="clinical-risk-classification" :class="classificationClass">
          <div class="clinical-risk-classification-icon">
            <i :class="badgeIcon"></i>
          </div>
          <div>
            <h2 class="m-0">{{ badgeLabel }}</h2>
            <p class="m-0" style="opacity: 0.9">
              Score total: {{ clinicalRisk.score }} pontos
            </p>
          </div>
        </div>

        <!-- Detalhamento dos pontos -->
        <div class="clinical-risk-detail-section">
          <h4 class="clinical-risk-section-title">
            <i class="fas fa-list-ul mr-2"></i>Detalhamento da Pontuação
          </h4>

          <div class="clinical-risk-detail-grid">
            <!-- Internação -->
            <div
              class="clinical-risk-detail-item"
              :class="
                detail.hospitalization_points > 0 ? 'item-alert' : 'item-ok'
              "
            >
              <div class="detail-label">
                <i class="fas fa-hospital mr-1"></i>
                Internação nos últimos 90 dias
                <span class="detail-sub">
                  ({{ detail.hospitalizations_last_90d }} ocorrência(s))
                </span>
              </div>
              <div
                class="detail-points"
                :class="
                  detail.hospitalization_points > 0
                    ? 'points-alert'
                    : 'points-ok'
                "
              >
                +{{ detail.hospitalization_points }}
              </div>
            </div>

            <!-- Infecção -->
            <div
              class="clinical-risk-detail-item"
              :class="detail.infection_points > 0 ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-virus mr-1"></i>
                Infecção nos últimos 90 dias
                <span class="detail-sub">
                  ({{ detail.infections_last_90d }} ocorrência(s))
                </span>
              </div>
              <div
                class="detail-points"
                :class="
                  detail.infection_points > 0 ? 'points-alert' : 'points-ok'
                "
              >
                +{{ detail.infection_points }}
              </div>
            </div>

            <!-- Antibiótico -->
            <div
              class="clinical-risk-detail-item"
              :class="detail.antibiotic_points > 0 ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-pills mr-1"></i>
                Antibiótico em uso
                <span
                  v-if="
                    detail.antibiotics_active &&
                    detail.antibiotics_active.length > 0
                  "
                  class="detail-sub"
                >
                  ({{ detail.antibiotics_active.join(", ") }})
                </span>
              </div>
              <div
                class="detail-points"
                :class="
                  detail.antibiotic_points > 0 ? 'points-alert' : 'points-ok'
                "
              >
                +{{ detail.antibiotic_points }}
              </div>
            </div>

            <!-- Pressão arterial -->
            <div
              class="clinical-risk-detail-item"
              :class="
                detail.blood_pressure_points > 0 ? 'item-alert' : 'item-ok'
              "
            >
              <div class="detail-label">
                <i class="fas fa-tachometer-alt mr-1"></i>
                Pressão arterial alterada (últimos 7 dias)
                <span v-if="detail.blood_pressure_last" class="detail-sub">
                  (último registro: {{ detail.blood_pressure_last }})
                </span>
                <span v-else class="detail-sub">(sem registro)</span>
              </div>
              <div
                class="detail-points"
                :class="
                  detail.blood_pressure_points > 0
                    ? 'points-alert'
                    : 'points-ok'
                "
              >
                +{{ detail.blood_pressure_points }}
              </div>
            </div>

            <!-- Febre -->
            <div
              class="clinical-risk-detail-item"
              :class="detail.fever_points > 0 ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-thermometer-half mr-1"></i>
                Febre detectada (últimos 7 dias)
                <span
                  v-if="detail.temperature_last !== null"
                  class="detail-sub"
                >
                  (último registro: {{ detail.temperature_last }}°C)
                </span>
                <span v-else class="detail-sub">(sem registro)</span>
              </div>
              <div
                class="detail-points"
                :class="detail.fever_points > 0 ? 'points-alert' : 'points-ok'"
              >
                +{{ detail.fever_points }}
              </div>
            </div>
          </div>

          <!-- Alerta crítico -->
          <div v-if="isCritical" class="clinical-critical-alert">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            Residente em estado crítico. Avaliação médica imediata recomendada.
          </div>

          <!-- Total -->
          <div class="clinical-risk-total" :class="totalClass">
            <span>Score Total</span>
            <span class="clinical-risk-total-value">
              {{ clinicalRisk.score }} pontos
            </span>
          </div>

          <!-- Legenda -->
          <div class="clinical-risk-legend">
            <span class="legend-item legend-verde">0–2 Baixo</span>
            <span class="legend-item legend-amarelo">3–5 Moderado</span>
            <span class="legend-item legend-vermelho">6–9 Alto</span>
            <span class="legend-item legend-critico">10+ Crítico</span>
          </div>
        </div>

        <!-- Footer -->
        <div class="clinical-risk-modal-footer">
          <small class="text-muted">
            <i class="fas fa-info-circle mr-1"></i>
            Score calculado com base nos dados cadastrados
          </small>
          <v-btn
            class="clinical-risk-recalculate-btn"
            small
            :loading="recalculating"
            :disabled="recalculating"
            @click="recalculate"
          >
            <i class="fas fa-sync-alt mr-1"></i>
            Recalcular
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Overlay -->
    <div v-if="showModal" class="clinical-risk-overlay"></div>
  </div>
</template>

<script>
import api from "../../services/api";
import moment from "moment";

export default {
  name: "ClinicalRiskBadge",

  props: {
    resident_id: [String, Number],
  },

  data() {
    return {
      loading: false,
      recalculating: false,
      showModal: false,
      clinicalRisk: null,
    };
  },

  computed: {
    detail() {
      if (!this.clinicalRisk || !this.clinicalRisk.detail) return {};
      return this.clinicalRisk.detail;
    },

    isCritical() {
      return this.clinicalRisk && this.clinicalRisk.level === "critico";
    },

    badgeClass() {
      const level = this.clinicalRisk && this.clinicalRisk.level;
      if (level === "critico") return "badge-critico";
      if (level === "alto") return "badge-vermelho";
      if (level === "moderado") return "badge-amarelo";
      return "badge-verde";
    },

    headerClass() {
      const level = this.clinicalRisk && this.clinicalRisk.level;
      if (level === "critico") return "header-critico";
      if (level === "alto") return "header-vermelho";
      if (level === "moderado") return "header-amarelo";
      return "header-verde";
    },

    classificationClass() {
      const level = this.clinicalRisk && this.clinicalRisk.level;
      if (level === "critico") return "classification-critico";
      if (level === "alto") return "classification-vermelho";
      if (level === "moderado") return "classification-amarelo";
      return "classification-verde";
    },

    totalClass() {
      const level = this.clinicalRisk && this.clinicalRisk.level;
      if (level === "critico") return "total-critico";
      if (level === "alto") return "total-vermelho";
      if (level === "moderado") return "total-amarelo";
      return "total-verde";
    },

    badgeLabel() {
      const level = this.clinicalRisk && this.clinicalRisk.level;
      if (level === "critico") return "Crítico";
      if (level === "alto") return "Alto";
      if (level === "moderado") return "Moderado";
      return "Baixo";
    },

    badgeIcon() {
      const level = this.clinicalRisk && this.clinicalRisk.level;
      if (level === "critico") return "fas fa-skull-crossbones";
      if (level === "alto") return "fas fa-exclamation-triangle";
      if (level === "moderado") return "fas fa-exclamation-circle";
      return "fas fa-check-circle";
    },

    updatedAt() {
      if (!this.clinicalRisk || !this.clinicalRisk.updated_at) return "—";
      return moment(this.clinicalRisk.updated_at).format("DD/MM/YYYY HH:mm");
    },
  },

  created() {
    this.loadScore();
  },

  methods: {
    async loadScore() {
      try {
        this.loading = true;
        const response = await api.get(`/ai/smart/summary/${this.resident_id}`);
        if (response.data.success) {
          this.clinicalRisk = response.data.clinical_risk;
        }
      } catch (error) {
        console.error("[ClinicalRiskBadge] erro ao carregar score:", error);
      } finally {
        this.loading = false;
      }
    },

    async recalculate() {
      try {
        this.recalculating = true;
        const response = await api.post(
          `/ai/smart/summary/${this.resident_id}/recalculate-clinical-risk`
        );
        if (response.data.success) {
          this.clinicalRisk = response.data.clinical_risk;
          this.$toast.success("Score clínico recalculado com sucesso!");
        } else {
          this.$toast.error("Erro ao recalcular score.");
        }
      } catch (error) {
        console.error("[ClinicalRiskBadge] erro ao recalcular:", error);
        this.$toast.error("Erro ao recalcular. Tente novamente.");
      } finally {
        this.recalculating = false;
      }
    },

    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
  },
};
</script>

<style scoped>
/* ============================================================
   Wrapper e Badge
   ============================================================ */
.clinical-risk-wrapper {
  display: inline-block;
  margin-top: 4px;
}

.clinical-risk-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  user-select: none;
}

.clinical-risk-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.clinical-risk-score {
  background: rgba(255, 255, 255, 0.9);
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  margin-left: 2px;
}

.badge-verde {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #065f46;
  border: 1px solid #6ee7b7;
}

.badge-amarelo {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #92400e;
  border: 1px solid #f59e0b;
}

.badge-vermelho {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #991b1b;
  border: 1px solid #f87171;
}

.badge-critico {
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  color: #4c1d95;
  border: 1px solid #a78bfa;
  animation: pulse-critico 2s infinite;
}

@keyframes pulse-critico {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(139, 92, 246, 0);
  }
}

.clinical-risk-loading {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
  cursor: default;
}

.clinical-risk-loading:hover {
  transform: none;
  box-shadow: none;
}

/* ============================================================
   Overlay e Modal
   ============================================================ */
.clinical-risk-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.clinical-risk-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.clinical-risk-modal-content {
  background: white;
  border-radius: 16px;
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ============================================================
   Header do Modal
   ============================================================ */
.clinical-risk-modal-header {
  padding: 20px 24px;
  border-radius: 16px 16px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}

.clinical-risk-modal-header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.clinical-risk-modal-header h3 {
  font-size: 1.2rem;
  font-weight: 600;
}

.header-verde {
  background: linear-gradient(135deg, #059669, #047857);
}
.header-amarelo {
  background: linear-gradient(135deg, #d97706, #b45309);
}
.header-vermelho {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
}
.header-critico {
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
}

.clinical-risk-close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
  line-height: 1;
}

.clinical-risk-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* ============================================================
   Classificação
   ============================================================ */
.clinical-risk-classification {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
}

.clinical-risk-classification-icon {
  font-size: 2rem;
}

.clinical-risk-classification h2 {
  font-size: 1.8rem;
  font-weight: 700;
}

.classification-verde {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  color: #065f46;
}
.classification-amarelo {
  background: linear-gradient(135deg, #fffbeb, #fef3c7);
  color: #92400e;
}
.classification-vermelho {
  background: linear-gradient(135deg, #fff1f2, #fee2e2);
  color: #991b1b;
}
.classification-critico {
  background: linear-gradient(135deg, #f5f3ff, #ede9fe);
  color: #4c1d95;
}

/* ============================================================
   Detalhamento
   ============================================================ */
.clinical-risk-detail-section {
  padding: 20px 24px;
}

.clinical-risk-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.clinical-risk-detail-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.clinical-risk-detail-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
}

.item-ok {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}
.item-alert {
  background: #fff7ed;
  border: 1px solid #fed7aa;
}

.detail-label {
  color: #374151;
  flex: 1;
  line-height: 1.5;
}

.detail-sub {
  color: #6b7280;
  font-size: 12px;
  margin-left: 4px;
}

.detail-points {
  font-weight: 700;
  font-size: 14px;
  min-width: 32px;
  text-align: right;
  padding-top: 1px;
}

.points-ok {
  color: #059669;
}
.points-alert {
  color: #d97706;
}

/* ============================================================
   Alerta crítico
   ============================================================ */
.clinical-critical-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f5f3ff, #ede9fe);
  border: 1px solid #a78bfa;
  border-radius: 10px;
  color: #4c1d95;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 12px;
}

/* ============================================================
   Total
   ============================================================ */
.clinical-risk-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 12px;
}

.total-verde {
  background: #d1fae5;
  color: #065f46;
}
.total-amarelo {
  background: #fef3c7;
  color: #92400e;
}
.total-vermelho {
  background: #fee2e2;
  color: #991b1b;
}
.total-critico {
  background: #ede9fe;
  color: #4c1d95;
}

.clinical-risk-total-value {
  font-size: 18px;
  font-weight: 700;
}

/* ============================================================
   Legenda
   ============================================================ */
.clinical-risk-legend {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.legend-item {
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.legend-verde {
  background: #d1fae5;
  color: #065f46;
}
.legend-amarelo {
  background: #fef3c7;
  color: #92400e;
}
.legend-vermelho {
  background: #fee2e2;
  color: #991b1b;
}
.legend-critico {
  background: #ede9fe;
  color: #4c1d95;
}

/* ============================================================
   Footer
   ============================================================ */
.clinical-risk-modal-footer {
  padding: 16px 24px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  border-radius: 0 0 16px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.clinical-risk-recalculate-btn {
  background: #0f65b0 !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

/* ============================================================
   Responsivo
   ============================================================ */
@media (max-width: 576px) {
  .clinical-risk-modal {
    padding: 10px;
  }

  .clinical-risk-modal-content {
    max-height: 95vh;
  }

  .clinical-risk-modal-footer {
    flex-direction: column;
    text-align: center;
  }
}
</style>